import { getPerformance } from 'firebase/performance';
import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';
import { app } from '../firebase';
import { logger } from '../utils/logger';
import { trackCustomEvent, trackTiming } from './analyticsService';

/**
 * Simplified Firebase Performance Monitoring Service
 * 
 * Features:
 * - Firebase Performance Monitoring initialization
 * - Core Web Vitals tracking (CLS, FID, FCP, LCP, TTFB)
 * - Basic custom trace creation
 */

// Performance instance
let performance: any = null;

// Active traces map
const activeTraces = new Map<string, any>();

/**
 * Initialize Firebase Performance Monitoring
 */
export const initializePerformanceMonitoring = (): boolean => {
  try {
    if (!performance) {
      performance = getPerformance(app);
      logger.info('⚡ Firebase Performance Monitoring initialized');
    }
    return true;
  } catch (error) {
    logger.error('❌ Failed to initialize Performance Monitoring:', error);
    return false;
  }
};

/**
 * Check if performance monitoring is ready
 */
const isPerformanceReady = (): boolean => {
  if (!performance) {
    logger.warn('⚠️ Performance monitoring not initialized');
    return false;
  }
  return true;
};

/**
 * Start a custom trace
 */
export const startTrace = (traceName: string): string => {
  if (!isPerformanceReady()) return '';

  try {
    const trace = performance.trace(traceName);
    trace.start();
    
    const traceId = `${traceName}_${Date.now()}`;
    activeTraces.set(traceId, trace);
    
    logger.info(`🏁 Started trace: ${traceName}`, { traceId });
    return traceId;
  } catch (error) {
    logger.error(`❌ Error starting trace ${traceName}:`, error);
    return '';
  }
};

/**
 * Stop a custom trace
 */
export const stopTrace = (traceId: string, attributes?: Record<string, string>): void => {
  if (!isPerformanceReady()) return;

  try {
    const trace = activeTraces.get(traceId);
    if (!trace) {
      logger.warn(`⚠️ Trace not found: ${traceId}`);
      return;
    }

    // Add custom attributes if provided
    if (attributes) {
      Object.entries(attributes).forEach(([key, value]) => {
        trace.putAttribute(key, value);
      });
    }

    trace.stop();
    activeTraces.delete(traceId);
    
    logger.success(`🏆 Stopped trace: ${traceId}`, { attributes });
  } catch (error) {
    logger.error(`❌ Error stopping trace ${traceId}:`, error);
  }
};

/**
 * Initialize Web Vitals tracking
 */
export const initializeWebVitals = (): void => {
  const reportWebVital = (metric: any) => {
    try {
      // Track in Firebase Analytics
      trackTiming(metric.name, metric.value, 'web_vitals');
      
      // Track as custom event with additional context
      trackCustomEvent('web_vital_measured', {
        metric_name: metric.name,
        metric_value: metric.value.toString(),
        metric_id: metric.id,
        page_location: window.location.href,
        user_agent: navigator.userAgent.substring(0, 100)
      });
      
      logger.info(`📈 Web Vital: ${metric.name} = ${metric.value}ms`, {
        id: metric.id
      });
    } catch (error) {
      logger.error(`❌ Error reporting web vital ${metric.name}:`, error);
    }
  };

  // Get all web vitals
  getCLS(reportWebVital);
  getFID(reportWebVital);
  getFCP(reportWebVital);
  getLCP(reportWebVital);
  getTTFB(reportWebVital);
  
  logger.info('📊 Web Vitals tracking initialized');
};

/**
 * Track page load performance
 */
export const trackPageLoadPerformance = (): void => {
  if (typeof window === 'undefined') return;

  window.addEventListener('load', () => {
    setTimeout(() => {
      try {
        const navigation = window.performance?.getEntriesByType('navigation')[0] as any;
        if (!navigation) return;

        const timings = {
          dns_lookup: Math.round(navigation.domainLookupEnd - navigation.domainLookupStart),
          tcp_connect: Math.round(navigation.connectEnd - navigation.connectStart),
          server_response: Math.round(navigation.responseStart - navigation.requestStart),
          page_download: Math.round(navigation.responseEnd - navigation.responseStart),
          dom_processing: Math.round(navigation.domContentLoadedEventStart - navigation.responseEnd),
          total_load_time: Math.round(navigation.loadEventEnd - navigation.fetchStart)
        };

        // Track individual timing metrics
        Object.entries(timings).forEach(([name, value]) => {
          if (value > 0) {
            trackTiming(name, value, 'page_load');
          }
        });

        // Track overall page load event
        trackCustomEvent('page_load_performance', {
          ...timings,
          page_location: window.location.href,
          referrer: document.referrer
        });

        logger.info('📊 Page load performance tracked', timings);
      } catch (error) {
        logger.error('❌ Error tracking page load performance:', error);
      }
    }, 100);
  });
};

/**
 * Start performance monitoring
 */
export const startPerformanceMonitoring = (): void => {
  // Initialize Firebase Performance
  initializePerformanceMonitoring();
  
  // Initialize Web Vitals tracking
  initializeWebVitals();
  
  // Track page load performance
  trackPageLoadPerformance();
  
  logger.success('🚀 Performance monitoring started');
};

export { performance as performanceInstance };
